import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Option, OptionDataValueInput, OptionFile, OptionFileUnion } from '../../../shared/options';
import { useTranslation } from '../../translations';
import { classNames, formatBytes } from '../../utils';
import { MaterialIcon } from '../MaterialIcon';

import './OptionFileComponent.scss';

export interface OptionFileProps {
  readonly option: Option;
  readonly defaultValue?: OptionDataValueInput;
  readonly onChange?: (id: number, option: OptionDataValueInput) => void;
}

export const OptionFileComponent: FC<OptionFileProps> = ({ option, onChange, defaultValue }) => {
  const [files, setFiles] = useState<OptionFileUnion[]>(
    (defaultValue as OptionFile[] | undefined) ?? [],
  );

  const { ts } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);
  const maxFiles = option.max ?? 4;
  const name = `options[${option.id}]`;
  // 5 MB file size limit
  const maxFileSize = 5 * 1024 * 1024;

  // TODO: onChange should never be called on initial render, but it does.
  useEffect(() => {
    if (onChange) {
      onChange(option.id, files);
    }
  }, [files]);

  const handleFileUpload = useCallback(
    (eventFiles: FileList) => {
      if (files.length + eventFiles.length > maxFiles) {
        toast.error(ts('max_files_allowed', maxFiles));
        // Remove any selected values
        if (inputRef.current) {
          inputRef.current.value = '';
        }

        return;
      }

      for (let i = 0; i < eventFiles.length; i++) {
        const file = eventFiles.item(i)!;

        if (file.size > maxFileSize) {
          toast.error(ts('file_too_large', file.name, formatBytes(maxFileSize)));
          continue;
        }

        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          const result = fileReader.result as string;
          const data = result.slice(result.indexOf('base64') + 7);

          // Add the uploaded file to the list
          setFiles((old) => [
            ...old,
            {
              contentType: file.type,
              fileName: file.name,
              size: file.size,
              data,
            },
          ]);
        };

        fileReader.readAsDataURL(file);
      }

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [files.length, maxFiles, ts, maxFileSize],
  );

  const onChangeFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFileUpload(e.target.files);
      }
    },
    [handleFileUpload],
  );

  const remove = useCallback((file: OptionFileUnion) => {
    setFiles((old) =>
      old.filter((oldFile) => {
        if (file.id && oldFile.id) {
          return file.id !== oldFile.id;
        }

        if (file.id === undefined && oldFile.id === undefined) {
          return file.data !== oldFile.data;
        }

        return true;
      }),
    );
  }, []);

  const onClickUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef]);

  return (
    <div>
      <div className="file-upload">
        <input
          accept="image/*,video/*,application/pdf"
          className="file-upload-input"
          multiple
          name={name}
          onChange={onChangeFile}
          ref={inputRef}
          type="file"
        />
        <FileUploadDroparea
          fileCount={files.length}
          maxFileSize={maxFileSize}
          maxFiles={maxFiles}
          onClickUpload={onClickUpload}
          onDropUpload={handleFileUpload}
        />
      </div>
      {files.map((file) => (
        <FileUploadItem
          file={file}
          key={typeof file === 'string' ? file : file.fileName}
          onClickRemove={() => {
            remove(file);
          }}
        />
      ))}
    </div>
  );
};

interface DropareaProps {
  readonly fileCount: number;
  readonly maxFileSize: number;
  readonly maxFiles: number;
  readonly onClickUpload: () => void;
  readonly onDropUpload: (f: FileList) => void;
}

const FileUploadDroparea: FC<DropareaProps> = ({
  fileCount,
  maxFileSize,
  maxFiles,
  onClickUpload,
  onDropUpload,
}) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else {
        setDragActive(false);
      }
    },
    [setDragActive],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      setDragActive(false);
      onDropUpload(e.dataTransfer.files);
    },
    [onDropUpload],
  );

  const { ts } = useTranslation();

  if (fileCount >= maxFiles) {
    return (
      <div className="file-upload-droparea disabled">
        <div className="file-upload-icon">
          <MaterialIcon large name="warning" type="warning" />
        </div>
        <div>{ts('option_file_component.max_files')}</div>
        <p>
          {ts('option_file_comment.files_number', maxFiles)}
          {maxFiles > 1 ? 's' : ''} {ts('option_file_comment.can_be_uploaded')}
        </p>
      </div>
    );
  }

  return (
    <div
      className={classNames({ 'drag-active': dragActive }, 'file-upload-droparea')}
      onClick={onClickUpload}
      onDragEnter={handleDrag}
      onDragExit={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <div className="file-upload-icon">
        <MaterialIcon large name="upload_file" />
      </div>
      {dragActive ? (
        <div>{ts('option_file_comment.drop_file_upload')}</div>
      ) : (
        <>
          <div>{ts('option_file_comment.select_file_upload')}</div>
          <p>{ts('option_file_comment.drag_drop')}</p>
        </>
      )}
      <p>
        {maxFiles === 1 ? (
          <>
            ({ts('max')} {formatBytes(maxFileSize)})
          </>
        ) : (
          <>({ts('max_files_multi', maxFiles, formatBytes(maxFileSize))})</>
        )}
      </p>
    </div>
  );
};

interface ItemProps {
  readonly file: OptionFileUnion;
  readonly onClickRemove: () => void;
}

const FileUploadItem: FC<ItemProps> = ({ file, onClickRemove }) => {
  if (file.id) {
    return (
      <div className="file-upload-item">
        <div className="file-upload-item-name">
          <MaterialIcon className="cloud" name="cloud" small />
          <a href={file.url} rel="noreferrer" target="_blank">
            {file.fileName}
          </a>
        </div>
        <div className="dont-shrink">{formatBytes(file.size)}</div>
        <div className="dont-shrink">
          <MaterialIcon className="delete" name="delete" onClick={onClickRemove} small />
        </div>
      </div>
    );
  }

  return (
    <div className="file-upload-item pending">
      <div className="file-upload-item-name">{file.fileName}</div>
      <div className="dont-shrink">{formatBytes(file.size)}</div>
      <div className="dont-shrink">
        <MaterialIcon className="delete" name="delete" onClick={onClickRemove} small />
      </div>
    </div>
  );
};
